'use client';

import React, { JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { 
	PROD_PRICE_CLASS_DECIMAL, 
	PROD_PRICE_CLASS_SYMBOL, 
} from '@/constants/product';
import {
	TX_CART_ITEM_COUNT,
	TX_CART_ITEM_COUNT_ONE,
	TX_CHECKOUT_NO_SHIPPING_METHODS,
	TX_DELIVERY,
	TX_FREE,
	TX_PICKUP,
} from '@/constants/strings';

import useCart from '@/hooks/useCart';

import Location from '@/models/locations/Location';
import Radio from '@/components/Input/Radio';
import ShippingMethod from '@/models/shipping/ShippingMethod';

import { formatPrice } from '@/utils/formatting';
import { getStoreLanguage } from '@/utils/language';

import stepStyles from '../_styles/CheckoutStepComponent.module.scss';
import styles from './_styles/ShippingMethodLocationInput.module.scss';


// Prop types
interface ShippingMethodLocationInputProps {
	disablePickup?: boolean;
	location: Location;
	shippingMethods: ShippingMethod[];
	selectedMethod: ShippingMethod | null;
	onSelect: (method: ShippingMethod) => void;
}

export const ShippingMethodLocationInput = (props: ShippingMethodLocationInputProps): JSX.Element => {
  
	// Props
	const {
		disablePickup = false,
		location,
		shippingMethods,
		selectedMethod,
		onSelect,
	} = props;

	// Cart
	const { activeCart } = useCart();

	// Internationalization
	const { 
		i18n,
		t, 
	} = useTranslation();

	// Methods
	const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
		evt.preventDefault();
		if(selectedMethod) {
			onSelect(selectedMethod);
		}
	};

	const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
		const methodUuid = evt.target.value;
		const method = shippingMethods.find(sm => sm.publicUuid === methodUuid) || null;

		if(method) {
			onSelect(method);
		}
	};

	const isOptionSelected = (option: ShippingMethod) => {
		if(!option?.publicUuid || !selectedMethod?.publicUuid) { return false; }
		return !!(selectedMethod.publicUuid === option.publicUuid);
	}

	// Derived constants
	const applicableShippingMethods = useMemo((): ShippingMethod[] => {
		
		if(!activeCart || !shippingMethods.length) {
			return [];
		}

		// FILTER LOCATIONS
		if(!activeCart.locations.find(loc => loc.publicUuid === location.publicUuid)) {
			return [];
		}

		const methodsResp: ShippingMethod[] = [];
		for(const ship of shippingMethods) {
			
			// Skip methods with bad costs
			if(ship.cost < 0) {
				continue;
			}

			// Check location
			if(ship.locations.length && !ship.locations.find(loc => loc.publicUuid === location.publicUuid)) {
				continue;
			}

			// Check min purchase
			if(!isNaN(ship.minPurchase) && activeCart.subTotalForLocation(location) >= ship.minPurchase) {
				methodsResp.push(ship);
			}
		}
		return methodsResp;

	}, [ activeCart, shippingMethods ]);

	const deliveryMethods = useMemo(() => {
		return applicableShippingMethods.filter(sm => !sm.isPickup);
	} , [ applicableShippingMethods ]);
	
	const pickupMethods = useMemo(() => {
		return applicableShippingMethods.filter(sm => sm.isPickup);
	} , [ applicableShippingMethods ]);

	const hasDeliveryOptions = deliveryMethods.length > 0;
	const hasPickupOptions = pickupMethods.length > 0;

	const storeLang = useMemo(() => getStoreLanguage(i18n), [ i18n ]);

	// Effects
	// None yet

	// Render
	return (
		<div className={styles.ShippingMethodLocationInput}>
			{(activeCart?.locations?.length && activeCart?.locations?.length > 1) && (
				<div className={styles.locationTitleWrapper}>
					<div className={styles.titleValue}>
						{location.name}
					</div>
					{activeCart?.quantityForLocation(location) && (
						<div className={styles.titleCount}>
							{activeCart.quantityForLocation(location) === 1 
								? t(TX_CART_ITEM_COUNT_ONE) 
								: t(TX_CART_ITEM_COUNT, { 
									count: activeCart.quantityForLocation(location) 
								})
							}
						</div>
					)}
				</div>
			)}
			{(applicableShippingMethods.length > 0) ?
				<form className={stepStyles.checkoutStepRadioForm} onSubmit={handleSubmit}>
					<div className={stepStyles.radioOptionsWrapper}>
						{!disablePickup && (
							<>
								{!!(hasDeliveryOptions && hasPickupOptions) && (
									<div className={stepStyles.radioOptionLabel}>{t(TX_PICKUP)}</div>
								)}
								{pickupMethods.map((opt, i) => {
									return <div key={opt.publicUuid || i} className={`${stepStyles.radioOptionElementWrapper} ${isOptionSelected(opt) ? stepStyles.selected : ''}`}>
										<div className={stepStyles.radioInputWrapper}>
											<Radio
												id={`sm-pu-${i}-${location.publicUuid}-${opt.publicUuid}`}
												name={`shipping-method-${location.publicUuid}`}
												adminTheme={false}
												disabled={false}
												value={opt.publicUuid}
												checked={isOptionSelected(opt)}
												onChange={handleChange} />
										</div>
										<label htmlFor={`sm-pu-${i}-${location.publicUuid}-${opt.publicUuid}`}>
											<div className={stepStyles.radioLabelWrapper}>
												<div className={stepStyles.radioTitle}>
													<span className={styles.methodName}>{opt.name}</span>
													<span className={styles.methodCost}>
														<span 
															className={styles.priceValue} 
															dangerouslySetInnerHTML={{
																__html: formatPrice(opt.cost, { 
																	addTags: true, 
																	tags: {
																		[PROD_PRICE_CLASS_SYMBOL]: styles.symbol,
																		[PROD_PRICE_CLASS_DECIMAL]: styles.decimal,
																	},
																	language: storeLang, 
																	zeroValue: t(TX_FREE) 
																})
															}} />
													</span>
												</div>
												<div className={stepStyles.radioSubtitle}>{opt.description}</div>
											</div>
										</label>
									</div>;
								})}
							</>
						)}
						{!!(hasDeliveryOptions && hasPickupOptions && !disablePickup) && (
							<div className={stepStyles.radioOptionLabel}>{t(TX_DELIVERY)}</div>
						)}
						{deliveryMethods.map((opt, j) => {
							return <div key={opt.publicUuid || j} className={`${stepStyles.radioOptionElementWrapper} ${isOptionSelected(opt) ? stepStyles.selected : ''}`}>
								<div className={stepStyles.radioInputWrapper}>
									<Radio
										id={`sm-del-${j}-${location.publicUuid}-${opt.publicUuid}`}
										name={`shipping-method-${location.publicUuid}`}
										adminTheme={false}
										disabled={false}
										value={opt.publicUuid}
										checked={isOptionSelected(opt)}
										onChange={handleChange} />
								</div>
								<label htmlFor={`sm-del-${j}-${location.publicUuid}-${opt.publicUuid}`}>
									<div className={stepStyles.radioLabelWrapper}>
										<div className={stepStyles.radioTitle}>
											<span className={styles.methodName}>{opt.name}</span>
											<span className={styles.methodCost}>
											<span 
												className={styles.priceValue} 
												dangerouslySetInnerHTML={{
													__html: formatPrice(opt.cost, { 
														addTags: true, 
														tags: {
															[PROD_PRICE_CLASS_SYMBOL]: styles.symbol,
															[PROD_PRICE_CLASS_DECIMAL]: styles.decimal,
														},
														language: storeLang, 
														zeroValue: t(TX_FREE) 
													})
												}} />
											</span>
										</div>
										<div className={stepStyles.radioSubtitle}>{opt.description}</div>
									</div>
								</label>
							</div>;
						})}
					</div>
				</form> :
				<div className={styles.noOptions}>
					{t(TX_CHECKOUT_NO_SHIPPING_METHODS)}
				</div>
			}
		</div>
	);
};

export default ShippingMethodLocationInput;




